import { ChangeUserPasswordDTO, UserDTO } from "../domain/models/dto/UserDTO";
import { User, UsersCollection } from "../domain/models/User";
import { UserService } from "../domain/services/UserService";
import userMapperFactory from "../mappers/UserMapper";
import { InfoResponse } from "../types/ApiResults";
import { BaseServiceImpl } from "./BaseServiceImpl";
import { ResponseMessage } from "../domain/enums/common/ResponseMessageEnum";
import Cookies from "js-cookie";

export class UserServiceImpl extends BaseServiceImpl implements UserService {
    static _instance: UserService;
    currentUser?: User;

    getToken(): string {
        return Cookies.get("token") || "";
    }

    clearToken(): void {
        Cookies.remove("token", { domain: window.location.hostname });
    }

    getCookie(name: string): string {
        return Cookies.get(name) || "";
    }

    private readonly userEndpoint = "/api/v1/user";
    private readonly usersEndpoint = "/api/v1/users";
    private readonly confirmRegisterEndpoint =
        "/api/v1/registration-confirmation";
    private readonly loginEndpoint = "/api/v1/login";
    private readonly logoutEndpoint = "/api/v1/logout";

    private constructor() {
        super();
    }

    static getInstance(): UserService {
        if (!UserServiceImpl._instance) {
            UserServiceImpl._instance = new UserServiceImpl();
        }
        return UserServiceImpl._instance;
    }

    /**
     * Gets the current authenticated user, caching the result.
     *
     * NOTE: use getCurrentUser() to fetch from the api directly.
     */
    async getUser(): Promise<User> {
        if (!this.currentUser) {
            this.currentUser = await this.getCurrentUser();
        }
        return this.currentUser;
    }

    /**
     * Fetches the current authenticated user from the api by token in cookie.
     *
     * NOTE: use getUser() to get the cached user instance.
     *
     */
    async getCurrentUser(): Promise<User | undefined> {
        try {
            return userMapperFactory().fromDTO(
                await this.get(this.userEndpoint)
            );
        } catch (err) {
            console.log("UserServiceImpl.getCurrentUser => ERROR:");
            console.log(err);
        }
    }

    async login(email: string, password: string): Promise<User> {
        if (!email || !password) {
            return;
        }
        const response = await this.post<ResponseMessage>(this.loginEndpoint, {
            email,
            password,
        });
        if (response === ResponseMessage.SUCCESS) {
            this.currentUser = await this.getCurrentUser();
        }
        return this.currentUser;
    }

    async logout(): Promise<boolean> {
        try {
            return !!(await this.get(this.logoutEndpoint));
        } catch (err) {
            console.log("UserServiceImpl.logout => ERROR:");
            console.log(err);
        }
    }

    async register(dto: UserDTO): Promise<User> {
        if (!dto?.email || !dto?.firstName || !dto?.lastName) {
            console.log("UserServiceImpl.register => ERROR: Invalid DTO");
            console.log(dto);
            return;
        }
        const mapper = userMapperFactory();
        this.currentUser = mapper.fromDTO(
            await this.post<UserDTO>(this.userEndpoint, dto)
        );
        return this.currentUser;
    }

    async changeUserPassword(dto: ChangeUserPasswordDTO): Promise<User> {
        if (
            !dto?.currentPassword ||
            !dto?.newPassword ||
            !dto?.confirmNewPassword
        ) {
            return;
        }
        const mapper = userMapperFactory();
        return mapper.fromDTO(
            await this.patch<UserDTO>(
                `${this.userEndpoint}/change-password`,
                dto
            )
        );
    }

    async getAllUsers(
        currentPage: number,
        currentLimit: number,
        filtersDTO: UserDTO = {} as UserDTO
    ): Promise<UsersCollection> {
        try {
            let url = `${this.usersEndpoint}?limit=${currentLimit}&page=${currentPage}`;
            Object.entries(filtersDTO).forEach(([key, value]) => {
                if (key && value !== null && value !== undefined) {
                    url += `&${key}=${value}`;
                }
            });
            return await this.get(url);
        } catch (err) {
            console.log("Error in UserServiceImpl.getAllUsers");
            console.log(err);
        }
    }

    async confirmRegistration(emailToken: string): Promise<boolean> {
        try {
            return (
                await this.post<InfoResponse>(this.confirmRegisterEndpoint, {
                    emailToken,
                })
            ).status;
        } catch (err) {
            console.log("UserServiceImpl.confirmRegistration => ERROR:");
            console.log(err);
        }
    }
}

export function userServiceFactory(): UserService {
    return UserServiceImpl.getInstance();
}
